import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { AnalysisHeader, MethodNotes, PrioritySignal } from './components/AnalysisHeader'
import { BriefForm } from './components/BriefForm'
import { Dictionary } from './components/Dictionary'
import { ChatPanel } from './components/ChatPanel'
import { InsightsPanel } from './components/InsightsPanel'
import { MembersPanel } from './components/MembersPanel'
import { FolderWeights } from './components/FolderWeights'
import { MatrixChart } from './components/MatrixChart'
import { TerritoryPanel } from './components/TerritoryPanel'
import type { AnalysisRow, DiscoverySummary, InsightBundle, MatrixResult } from './lib/api'
import { api } from './lib/api'
import type { AuthState, Session } from './lib/auth'
import { boot, can, logout, redirectToHub } from './lib/auth'
import { KPI_LABELS } from './lib/labels'

const SessionContext = createContext<Session | null>(null)

function useSession(): Session {
  const session = useContext(SessionContext)
  if (!session) throw new Error('useSession fora do AuthGate')
  return session
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' })

  useEffect(() => {
    boot()
      .then(setAuth)
      .catch((exc: Error) => setAuth({ status: 'failed', message: exc.message }))
  }, [])

  // Enquanto autentica — inclusive durante o redirect para o Hub —, tela preta e nada
  // mais. Regra da casa: nem spinner nem "Entrando…" entre a pessoa e o login.
  if (auth.status === 'loading') return <div className="auth-blank" />
  if (auth.status === 'failed') return <Failure message={auth.message} />
  if (auth.status === 'refused') {
    return (
      <div className="app-shell">
        <main className="failure-state">
          <p className="eyebrow">Acesso não liberado</p>
          <h1>{auth.message}</h1>
          <p>Peça acesso a quem administra o projeto no Hub Galeria, ou entre com outra conta.</p>
          <button type="button" onClick={redirectToHub}>Entrar em {auth.config.project_name}</button>
        </main>
      </div>
    )
  }
  return (
    <SessionContext.Provider value={auth.session}>
      <Routes />
    </SessionContext.Provider>
  )
}

function Routes() {
  const session = useSession()
  const [path, setPath] = useState(window.location.pathname)

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname)
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const go = useCallback((next: string) => {
    window.history.pushState({}, '', next)
    setPath(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])
  const open = (id: string) => go(`/analyses/${encodeURIComponent(id)}`)

  const match = path.match(/^\/analyses\/([^/]+)/)
  if (match) return <AnalysisView analysisId={decodeURIComponent(match[1])} onBack={() => go('/')} />
  if (path === '/dicionario') return <DictionaryView onBack={() => go('/')} />
  if (path === '/membros') return <MembersView onBack={() => go('/')} />
  if (path === '/nova' && can(session, 'analyst')) {
    return <NewAnalysisView onBack={() => go('/')} onCreated={open} />
  }
  return <AnalysisListView onOpen={open} onNew={can(session, 'analyst') ? () => go('/nova') : undefined} />
}

function NewAnalysisView({ onBack, onCreated }: { onBack: () => void; onCreated: (id: string) => void }) {
  return (
    <div className="app-shell">
      <TopBar onBack={onBack} />
      <main className="home-page">
        <button type="button" className="back-link" onClick={onBack}>
          <span aria-hidden="true">←</span> Todas as análises
        </button>
        <section className="home-hero">
          <div>
            <p className="eyebrow">Nova análise</p>
            <h1>Audiência, marca <em>e objetivo.</em></h1>
          </div>
          <div className="hero-deck">
            <p>
              O KPI muda o cálculo do ranking, não só o texto. A leitura estratégica é gerada
              depois, sobre a matriz pronta.
            </p>
          </div>
        </section>
        <BriefForm onCreated={onCreated} />
      </main>
      <Footer />
    </div>
  )
}

function MembersView({ onBack }: { onBack: () => void }) {
  const session = useSession()
  // Acesso negado tem tela, não redirect: quem chegou aqui por link precisa saber por
  // que não vê nada. O backend recusa com 403 de qualquer forma.
  if (!can(session, 'admin')) {
    return <Failure message="A tela de membros é só para admins deste projeto." onBack={onBack} />
  }
  return (
    <div className="app-shell">
      <TopBar onBack={onBack} />
      <main className="home-page">
        <button type="button" className="back-link" onClick={onBack}>
          <span aria-hidden="true">←</span> Todas as análises
        </button>
        <section className="home-hero">
          <div>
            <p className="eyebrow">Administração</p>
            <h1>Quem entra, <em>e o que pode fazer.</em></h1>
          </div>
          <div className="hero-deck">
            <p>
              Papéis valem a partir do próximo login da pessoa. Quem entra sozinho pelo Hub
              chega como leitura; promover a analista libera criar análises, que usam cota.
            </p>
          </div>
        </section>
        <MembersPanel />
      </main>
      <Footer />
    </div>
  )
}

function DictionaryView({ onBack }: { onBack: () => void }) {
  return (
    <div className="app-shell">
      <TopBar onBack={onBack} />
      <main className="home-page">
        <Dictionary />
      </main>
      <Footer />
    </div>
  )
}

function BrandMark() {
  return (
    <div className="brand-mark" aria-label="gal territórios">
      <span className="brand-monogram">G</span>
      <span className="brand-wordmark">
        <strong>GAL</strong>
        <span>/ territórios</span>
      </span>
    </div>
  )
}

function TopBar({ onBack }: { onBack?: () => void }) {
  return (
    <header className="topbar">
      <button
        type="button"
        className={`brand-button${onBack ? ' brand-button-clickable' : ''}`}
        onClick={onBack}
        disabled={!onBack}
        aria-label={onBack ? 'Voltar para análises' : undefined}
      >
        <BrandMark />
      </button>
      <div className="topbar-right">
        {/* ponytail: link comum recarrega a SPA; vale virar navegação interna se incomodar */}
        <a className="topbar-link" href="/dicionario">Dicionário</a>
        <div className="topbar-status">
          <span className="status-dot" />
          motor determinístico
        </div>
        <UserChip />
      </div>
    </header>
  )
}

function UserChip() {
  const session = useContext(SessionContext)
  if (!session) return null
  const display = session.name ?? session.email
  return (
    <div className="user-chip">
      {session.photo_url ? (
        // Sem referrer: as fotos do Google respondem 403 quando recebem um.
        <img src={session.photo_url} alt="" referrerPolicy="no-referrer" />
      ) : (
        <span className="user-initials" aria-hidden="true">{initials(display)}</span>
      )}
      <span className="user-name" title={session.email}>{display}</span>
      <span className="user-role">{ROLE_LABELS[session.role]}</span>
      {can(session, 'admin') && <a className="user-logout" href="/membros">Membros</a>}
      <button type="button" className="user-logout" onClick={logout}>Sair</button>
    </div>
  )
}

const ROLE_LABELS: Record<Session['role'], string> = {
  viewer: 'leitura',
  analyst: 'analista',
  admin: 'admin',
}

function initials(value: string): string {
  const parts = value.split(/[\s@.]+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?'
}

function AnalysisListView({ onOpen, onNew }: { onOpen: (id: string) => void; onNew?: () => void }) {
  const [rows, setRows] = useState<AnalysisRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .analyses()
      .then((data) => setRows(data ?? []))
      .catch((exc: Error) => setError(exc.message))
  }, [])

  if (error) return <Failure message={error} />

  const latest = rows?.[0]
  const remaining = rows?.slice(1) ?? []
  const totals = rows?.reduce(
    (acc, row) => ({
      territories: acc.territories + row.n_points,
      withInsights: acc.withInsights + Number(row.has_insights),
    }),
    { territories: 0, withInsights: 0 },
  )

  return (
    <div className="app-shell">
      <TopBar />
      <main className="home-page">
        <section className="home-hero">
          <div>
            <p className="eyebrow">Inteligência de marca / Brasil</p>
            <h1>Territórios</h1>
            <p className="hero-subtitle">
              Decisões de marca, <em>com o dado à vista.</em>
            </p>
          </div>
          <div className="hero-deck">
            <p>
              Encontre os territórios culturais onde uma audiência já está — e veja quem
              disputa cada espaço antes de decidir onde a marca deve entrar.
            </p>
            <span>Profiles · cálculo reproduzível · recomendações citadas</span>
            {onNew && <button type="button" className="primary-button" onClick={onNew}>Nova análise</button>}
          </div>
        </section>

        <section className="home-index" aria-label="Resumo do acervo">
          <div>
            <span className="index-value">{rows?.length ?? '—'}</span>
            <span className="index-label">análises salvas</span>
          </div>
          <div>
            <span className="index-value">{totals?.territories ?? '—'}</span>
            <span className="index-label">territórios medidos</span>
          </div>
          <div>
            <span className="index-value">{totals?.withInsights ?? '—'}</span>
            <span className="index-label">leituras estratégicas</span>
          </div>
          <div className="index-method">
            <span>Os números são calculados em código.</span>
            <span>A IA interpreta — e cita a origem.</span>
          </div>
        </section>

        {!rows && <Loading label="Abrindo o acervo" />}

        {rows && rows.length === 0 && (
          <section className="empty-state">
            <span className="empty-index">00</span>
            <div>
              <p className="eyebrow">Acervo vazio</p>
              <h2>A primeira análise ainda não foi gravada.</h2>
              <p>
                Assim que o motor concluir uma matriz, ela aparecerá aqui com seu recorte,
                wave, territórios e estado dos insights.
              </p>
            </div>
          </section>
        )}

        {latest && (
          <section className="latest-section">
            <div className="section-label">
              <span>01</span>
              <p>Leitura mais recente</p>
            </div>
            <button className="featured-analysis" type="button" onClick={() => onOpen(latest.analysis_id)}>
              <div className="featured-kicker">
                <span>{latest.has_insights ? 'pronta para decisão' : 'matriz calculada'}</span>
                <span>wave {latest.wave ?? 'não informada'}</span>
              </div>
              <div className="featured-body">
                <div>
                  <p className="eyebrow">{kpiLabel(latest.kpi)} / {latest.focus}</p>
                  <h2>{latest.label ?? latest.audience_label}</h2>
                  <p className="featured-audience">Audiência · {latest.audience_label}</p>
                </div>
                <div className="featured-stats">
                  <Stat value={latest.n_points} label="territórios" />
                  <Stat value={latest.n_suppressed} label="alertas de base" />
                </div>
              </div>
              <div className="featured-foot">
                <span>{formatDate(latest.generated_at)}</span>
                <span className="open-label">Abrir leitura <span aria-hidden="true">↗</span></span>
              </div>
            </button>
          </section>
        )}

        {remaining.length > 0 && (
          <section className="archive-section">
            <div className="section-label">
              <span>02</span>
              <p>Arquivo de análises</p>
            </div>
            <div className="analysis-grid">
              {remaining.map((row, index) => (
                <button
                  className="analysis-card"
                  type="button"
                  key={row.analysis_id}
                  onClick={() => onOpen(row.analysis_id)}
                >
                  <span className="card-index">{String(index + 2).padStart(2, '0')}</span>
                  <div>
                    <p className="eyebrow">{kpiLabel(row.kpi)}</p>
                    <h3>{row.label ?? row.audience_label}</h3>
                    <p>{row.audience_label} · {row.focus}</p>
                  </div>
                  <dl>
                    <div><dt>Territórios</dt><dd>{row.n_points}</dd></div>
                    <div><dt>Wave</dt><dd>{row.wave ?? '—'}</dd></div>
                    <div><dt>Leitura</dt><dd>{row.has_insights ? 'Disponível' : 'Pendente'}</dd></div>
                  </dl>
                  <span className="card-arrow" aria-hidden="true">↗</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  )
}

function AnalysisView({ analysisId, onBack }: { analysisId: string; onBack: () => void }) {
  const session = useSession()
  const [result, setResult] = useState<MatrixResult | null>(null)
  const [bundle, setBundle] = useState<InsightBundle | null>(null)
  const [discovery, setDiscovery] = useState<DiscoverySummary | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // O peso por pasta só muda o score ajustado, que a API reaplica na leitura: basta
  // reler a matriz.
  const reloadMatrix = () => {
    api.matrix(analysisId).then((matrix) => matrix && setResult(matrix)).catch((exc: Error) => setError(exc.message))
  }

  useEffect(() => {
    let cancelled = false
    Promise.all([api.matrix(analysisId), api.insights(analysisId), api.discovery(analysisId)])
      .then(([matrix, insights, sweep]) => {
        if (cancelled) return
        if (!matrix) {
          setError(`A análise ${analysisId} não foi encontrada.`)
          return
        }
        setResult(matrix)
        setBundle(insights)
        setDiscovery(sweep)
      })
      .catch((exc: Error) => !cancelled && setError(exc.message))
    return () => {
      cancelled = true
    }
  }, [analysisId])

  const selected = useMemo(
    () => result?.points.find((point) => point.territory_id === selectedId) ?? null,
    [result, selectedId],
  )

  if (error) return <Failure message={error} onBack={onBack} />

  return (
    <div className="app-shell analysis-shell">
      <TopBar onBack={onBack} />
      {!result && <Loading label="Montando a leitura" />}
      {result && (
        <main className="analysis-page">
          <button type="button" className="back-link" onClick={onBack}>
            <span aria-hidden="true">←</span> Todas as análises
          </button>
          <AnalysisHeader result={result} />
          <MatrixChart result={result} selectedId={selectedId} onSelect={setSelectedId} />
          <FolderWeights
            analysisId={analysisId}
            result={result}
            onSaved={can(session, 'analyst') ? reloadMatrix : undefined}
          />
          <PrioritySignal result={result} />
          <InsightsPanel
            bundle={bundle}
            result={result}
            onSelectTerritory={setSelectedId}
            onGenerate={
              can(session, 'analyst')
                ? () => api.generateInsights(analysisId).then(setBundle)
                : undefined
            }
          />
          <ChatPanel
            analysisId={analysisId}
            result={result}
            onSelectTerritory={setSelectedId}
            canChat={can(session, 'analyst')}
          />
          <MethodNotes result={result} discovery={discovery} />
        </main>
      )}
      {selected && (
        <TerritoryPanel point={selected} points={result?.points ?? []} bundle={bundle} onClose={() => setSelectedId(null)} />
      )}
      <Footer />
    </div>
  )
}

function Stat({ value, label }: { value: number; label: string }) {
  return <div><strong>{value}</strong><span>{label}</span></div>
}

function Loading({ label }: { label: string }) {
  return (
    <div className="loading-state" role="status">
      <span className="loading-mark" />
      <span>{label}</span>
    </div>
  )
}

function Failure({ message, onBack }: { message: string; onBack?: () => void }) {
  return (
    <div className="app-shell">
      <TopBar onBack={onBack} />
      <main className="failure-state">
        <p className="eyebrow">Não foi possível abrir</p>
        <h1>Esta leitura está fora do alcance agora.</h1>
        <p>{message}</p>
        {onBack && <button type="button" onClick={onBack}>Voltar ao acervo</button>}
      </main>
    </div>
  )
}

function Footer() {
  return (
    <footer className="site-footer">
      <BrandMark />
      <p>Profiles+ Brasil · inteligência que preserva a origem do dado.</p>
      <span>Internal / 2026</span>
    </footer>
  )
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(new Date(value))
}

function kpiLabel(value: string): string {
  return KPI_LABELS[value] ?? value
}
